import {
  ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  getCountries,
  getCountryCallingCode,
} from 'react-phone-number-input';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import flagsRaw from 'react-phone-number-input/flags';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import labelsRaw from 'react-phone-number-input/locale/en.json';

// Ported from the admin's PhoneInput so the website's contact form gets
// the same flag + dial-code picker. Same library, restyled to match
// Cherry Picked's DM Sans + cherry-red palette.

const FLAGS = flagsRaw as Record<
  string,
  React.ComponentType<{ title: string }>
>;

const LABELS = labelsRaw as Record<string, string>;

export type Country = string;

export type PhoneValue = {
  country: Country;
  mobileNumber: string;
};

export const getDialCode = (country: Country): string => {
  try {
    return `+${getCountryCallingCode(country as any)}`;
  } catch {
    return '';
  }
};

export const guessCountryFromDialCode = (
  dialCode?: string | null,
): Country | undefined => {
  if (!dialCode) return undefined;
  const dc = dialCode.replace('+', '').trim();
  if (!dc) return undefined;
  return getCountries().find((c) => getCountryCallingCode(c as any) === dc);
};

export const normaliseMobileNumber = (
  mobileNumber: string | null | undefined,
  country?: Country,
): string => {
  const digits = (mobileNumber ?? '').replace(/\D/g, '');
  if (!digits) return '';
  if (country) {
    let dial = '';
    try {
      dial = String(getCountryCallingCode(country as any) ?? '');
    } catch {
      dial = '';
    }
    if (dial && digits.startsWith(dial)) {
      return digits.slice(dial.length);
    }
  }
  return digits;
};

type CountryRow = { code: Country; name: string; dial: string };

const buildCountryList = (): CountryRow[] =>
  getCountries()
    .map((code) => ({
      code,
      name: LABELS[code] ?? code,
      dial: `+${getCountryCallingCode(code as any)}`,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

type CountrySelectorProps = {
  value: Country;
  disabled?: boolean;
  onChange: (next: Country) => void;
  hasError?: boolean;
};

function CountrySelector({
  value,
  disabled,
  onChange,
  hasError,
}: CountrySelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const countries = useMemo(buildCountryList, []);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onDocClick);
    window.addEventListener('keydown', onKey);
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      window.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries;
    const qDigits = q.replace('+', '');
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dial.includes(qDigits),
    );
  }, [countries, query]);

  const Flag = FLAGS[value];
  const dial = getDialCode(value);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex h-full items-center gap-2 rounded-l-[8px] border-r bg-[#fafaf7] px-3 font-['DM_Sans',sans-serif] text-[14px] font-bold text-[#3a3335] hover:bg-[#f2eee2]/70 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
          hasError ? 'border-red-500' : 'border-[#ede8e9]'
        }`}
      >
        {Flag ? (
          <span className="inline-flex h-4 w-6 overflow-hidden rounded-sm">
            <Flag title={value} />
          </span>
        ) : (
          <span className="inline-block h-4 w-6 rounded-sm bg-[#ede8e9]" />
        )}
        <span>{dial || '+'}</span>
        <span className="material-icons text-[#908e8e] text-[18px]">
          expand_more
        </span>
      </button>

      {open ? (
        <div
          role="listbox"
          className="absolute left-0 top-full z-30 mt-1 w-72 overflow-hidden rounded-[12px] border border-[#ede8e9] bg-white shadow-xl"
        >
          <div className="flex items-center gap-2 border-b border-[#f0f0f0] px-3 py-2">
            <span className="material-icons text-[#908e8e] text-[18px]">
              search
            </span>
            <input
              ref={inputRef}
              type="text"
              placeholder="Search country or code…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              className="w-full bg-transparent font-['DM_Sans',sans-serif] text-[14px] text-[#3a3335] outline-none placeholder:text-[#908e8e]"
            />
          </div>
          <ul className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 font-['DM_Sans',sans-serif] text-[14px] text-[#908e8e]">
                No matches.
              </li>
            ) : (
              filtered.map((c) => {
                const F = FLAGS[c.code];
                const selected = c.code === value;
                return (
                  <li key={c.code}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => {
                        onChange(c.code);
                        setOpen(false);
                        setQuery('');
                      }}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left font-['DM_Sans',sans-serif] text-[14px] transition-colors ${
                        selected
                          ? 'bg-[#8e2a2f] text-white'
                          : 'text-[#3a3335] hover:bg-[#fcfaf7] hover:text-[#8e2a2f]'
                      }`}
                    >
                      {F ? (
                        <span className="inline-flex h-4 w-6 overflow-hidden rounded-sm">
                          <F title={c.code} />
                        </span>
                      ) : null}
                      <span className="flex-1 truncate">{c.name}</span>
                      <span
                        className={`text-[12px] ${selected ? 'text-white/80' : 'text-[#908e8e]'}`}
                      >
                        {c.dial}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

type Props = {
  label?: ReactNode;
  error?: string | null;
  hint?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  value: PhoneValue;
  placeholder?: string;
  maxLength?: number;
  onChange: (next: PhoneValue) => void;
};

export function PhoneInput({
  label,
  error,
  hint,
  required,
  disabled,
  value,
  placeholder = 'Mobile number',
  maxLength = 15,
  onChange,
}: Props) {
  const inputId = useId();
  const hasError = !!error;

  return (
    <div className="space-y-1">
      {label ? (
        <label
          htmlFor={inputId}
          className="block font-['DM_Sans',sans-serif] text-[12px] font-bold text-[#908e8e]"
        >
          {label}
          {required ? (
            <span aria-hidden="true" className="ml-0.5 text-red-500">
              *
            </span>
          ) : null}
        </label>
      ) : null}
      <div
        className={`flex w-full items-stretch rounded-[8px] border bg-white transition-colors ${
          hasError
            ? 'border-red-500'
            : 'border-[#ede8e9] focus-within:border-[#8e2a2f]'
        } ${disabled ? 'opacity-60' : ''}`}
      >
        <CountrySelector
          value={value.country}
          disabled={disabled}
          onChange={(country) =>
            onChange({ country, mobileNumber: value.mobileNumber })
          }
          hasError={hasError}
        />
        <input
          id={inputId}
          type="tel"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          disabled={disabled}
          value={value.mobileNumber}
          maxLength={maxLength}
          onChange={(e) =>
            onChange({
              country: value.country,
              mobileNumber: normaliseMobileNumber(
                e.target.value,
                value.country,
              ),
            })
          }
          className="h-12 w-full rounded-r-[8px] bg-white px-4 font-['DM_Sans',sans-serif] text-[14px] font-bold text-[#3a3335] outline-none placeholder:text-[#908e8e] placeholder:font-normal disabled:cursor-not-allowed"
        />
      </div>
      {hasError ? (
        <p className="font-['DM_Sans',sans-serif] text-[12px] text-red-500">
          {error}
        </p>
      ) : hint ? (
        <p className="font-['DM_Sans',sans-serif] text-[12px] text-[#908e8e]">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export const emptyPhoneValue = (defaultCountry: Country = 'GB'): PhoneValue => ({
  country: defaultCountry,
  mobileNumber: '',
});
